import {
  ResizablePanelDirection,
  ResizablePanelWidth,
} from '../constants';
import { GripVertical } from 'lucide-react';

import { useCallback, useEffect, useRef, useState } from 'react';

export function useResizablePanels() {
  const [leftWidth, setLeftWidth] = useState(ResizablePanelWidth.DEFAULT_LEFT);
  const [rightWidth, setRightWidth] = useState(
    ResizablePanelWidth.DEFAULT_RIGHT
  );
  const isResizing = useRef<ResizablePanelDirection | null>(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback(
    (direction: ResizablePanelDirection) => (e: React.MouseEvent) => {
      e.preventDefault();
      isResizing.current = direction;
      startX.current = e.clientX;
      startWidth.current =
        direction === ResizablePanelDirection.LEFT ? leftWidth : rightWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [leftWidth, rightWidth]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;

      const delta = e.clientX - startX.current;
      const newWidth =
        isResizing.current === ResizablePanelDirection.LEFT
          ? startWidth.current + delta
          : startWidth.current - delta;

      const clampedWidth = Math.min(
        ResizablePanelWidth.MAX,
        Math.max(ResizablePanelWidth.MIN, newWidth)
      );

      if (isResizing.current === ResizablePanelDirection.LEFT) {
        setLeftWidth(clampedWidth);
      } else {
        setRightWidth(clampedWidth);
      }
    };

    const handleMouseUp = () => {
      isResizing.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return { leftWidth, rightWidth, handleMouseDown };
}

export function ResizeHandle({
  onMouseDown,
}: {
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      className="w-1 shrink-0 bg-transparent hover:bg-stroke-2 cursor-col-resize transition-colors group relative"
      onMouseDown={onMouseDown}
    >
      <div className="absolute inset-y-0 -left-1 -right-1" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical className="w-3 h-3 text-text-tertiary" />
      </div>
    </div>
  );
}
